import React, { useEffect, useState } from "react";
import { adminApi, formatApiError, DEV_MODE } from "../../lib/ledgerApi";
import HashString from "../../components/HashString";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Building2, Banknote } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["NEO", "USD", "EUR", "GBP", "INR", "JPY", "AED"];

export default function AdminBusinesses() {
  const [rows, setRows] = useState([]);
  const [fundOpen, setFundOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await adminApi.listBusinesses();
      setRows(r);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };
  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, []);

  const onFund = async () => {
    if (!active || !amount) return;
    setBusy(true);
    try {
      await adminApi.fund({
        business_id: active.id,
        currency,
        amount,
      });
      toast.success("Mint queued", { description: `${amount} ${currency} → ${active.legal_name}` });
      setFundOpen(false);
      setAmount("");
      load();
    } catch (e) {
      toast.error("Fund failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Super-admin · Businesses</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
          <Building2 className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
          Businesses
        </h1>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border bg-muted/30">
          <div className="col-span-3">Business</div>
          <div className="col-span-2">VPA</div>
          <div className="col-span-2">Admin</div>
          <div className="col-span-1">Wallets</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-12 gap-3 px-4 py-4 items-center border-b border-border last:border-b-0">
            <div className="col-span-3">
              <div className="font-medium">{r.legal_name}</div>
              <div className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">{r.country || "—"}</div>
            </div>
            <div className="col-span-2 min-w-0"><HashString value={r.full_vpa} label="VPA" /></div>
            <div className="col-span-2 text-xs font-mono text-muted-foreground truncate">{r.admin_email || "—"}</div>
            <div className="col-span-1 font-mono text-sm">{r.wallet_count}</div>
            <div className="col-span-2">
              <Select
                value={r.business_type || "standard"}
                onValueChange={async (v) => {
                  try {
                    await adminApi.setBusinessType(r.id, v);
                    toast.success("Type updated", { description: `${r.legal_name} → ${v}` });
                    load();
                  } catch (e) {
                    toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) });
                  }
                }}
              >
                <SelectTrigger
                  className="rounded-sm h-8 text-xs font-mono"
                  data-testid={`biz-type-${r.vpa_handle}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">standard</SelectItem>
                  <SelectItem value="audit_mirror">audit_mirror</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 text-right">
              {DEV_MODE && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`admin-fund-${r.vpa_handle}`}
                  className="rounded-sm border-border hover:border-foreground"
                  onClick={() => {
                    setActive(r);
                    setFundOpen(true);
                  }}
                >
                  <Banknote className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Fund
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={fundOpen} onOpenChange={setFundOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Fund {active?.legal_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="rounded-sm font-mono mt-1" data-testid="fund-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount</Label>
              <Input data-testid="fund-amount" type="number" min="0" step="0.000001" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-sm font-mono mt-1" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Treasury mint — recorded as a coinbase tx on the ledger. DEV-only endpoint.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFundOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={onFund} disabled={busy || !amount} data-testid="fund-confirm" className="rounded-sm">
              {busy ? "Minting…" : "Mint"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
